import React, { useState, useEffect, useCallback } from 'react';
import { Globe, RefreshCw, Play, AlertTriangle, CheckCircle2, Loader2, Undo2, History, ShieldAlert } from 'lucide-react';
import { toast } from 'sonner';
import logger from '../../utils/logger';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const authHeaders = () => {
  const token = localStorage.getItem('auth_token');
  return token ? { Authorization: `Bearer ${token}` } : {};
};

const ResultBadge = ({ label, value, color, testId }) => (
  <div className={`flex-1 min-w-[100px] bg-${color}-500/10 border border-${color}-500/30 rounded p-3 text-center`} data-testid={testId}>
    <div className={`text-2xl font-bold text-${color}-400`}>{value}</div>
    <div className="text-xs text-gray-400 mt-1">{label}</div>
  </div>
);

const TraceRow = ({ row, index }) => {
  const isResolved = !!row.resolved;
  return (
    <tr className="border-b border-gray-700/50" data-testid={`backfill-trace-row-${index}`}>
      <td className="py-2 px-3 text-xs text-gray-400 font-mono">{row.user_id}</td>
      <td className="py-2 px-3 text-xs">
        {isResolved
          ? <span className="text-green-400 font-medium">{row.resolved}</span>
          : <span className="text-gray-500 italic">unresolved</span>}
      </td>
      <td className="py-2 px-3 text-xs text-gray-500 break-all">{row.source}</td>
    </tr>
  );
};

const ResultsBlock = ({ result, isPreview }) => (
  <div className="space-y-3" data-testid={isPreview ? 'backfill-preview-results' : 'backfill-apply-results'}>
    <div className="flex flex-wrap gap-2">
      <ResultBadge label="candidates" value={result.candidates} color="gray" testId="backfill-result-candidates" />
      <ResultBadge label="resolved" value={result.resolved} color="green" testId="backfill-result-resolved" />
      {!isPreview && <ResultBadge label="applied" value={result.applied} color="blue" testId="backfill-result-applied" />}
      <ResultBadge label="skipped" value={result.skipped} color="amber" testId="backfill-result-skipped" />
    </div>
    {result.rows && result.rows.length > 0 && (
      <details className="bg-gray-900/50 border border-gray-700 rounded">
        <summary className="px-3 py-2 text-sm text-gray-300 cursor-pointer hover:bg-gray-700/30">
          Per-user trace ({result.rows.length} {result.rows.length === 1 ? 'row' : 'rows'}) — click to expand
        </summary>
        <div className="overflow-x-auto max-h-[400px]">
          <table className="w-full text-left">
            <thead className="bg-gray-800 sticky top-0">
              <tr>
                <th className="py-2 px-3 text-xs text-gray-400 font-medium">User ID</th>
                <th className="py-2 px-3 text-xs text-gray-400 font-medium">Resolved Country</th>
                <th className="py-2 px-3 text-xs text-gray-400 font-medium">Source</th>
              </tr>
            </thead>
            <tbody>
              {result.rows.map((r, i) => <TraceRow key={r.user_id} row={r} index={i} />)}
            </tbody>
          </table>
        </div>
      </details>
    )}
  </div>
);

export const CountryBackfillPanel = ({ onApplied }) => {
  const [missing, setMissing] = useState(null);
  const [loadingStats, setLoadingStats] = useState(false);
  const [preview, setPreview] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [applyResult, setApplyResult] = useState(null);
  const [applyLoading, setApplyLoading] = useState(false);
  const [confirming, setConfirming] = useState(false);

  // Recovery sub-panel state
  const [runs, setRuns] = useState([]);
  const [undoLoading, setUndoLoading] = useState(false);
  const [recoveryCountry, setRecoveryCountry] = useState('United States');
  const [recoverySince, setRecoverySince] = useState(0);
  const [recoveryPreview, setRecoveryPreview] = useState(null);
  const [recoveryBusy, setRecoveryBusy] = useState(false);
  const [showRecovery, setShowRecovery] = useState(false);

  const loadStats = useCallback(async () => {
    setLoadingStats(true);
    try {
      const res = await fetch(`${API_URL}/api/admin/users/country-backfill/stats`, { headers: authHeaders() });
      if (!res.ok) throw new Error(`Stats failed (${res.status})`);
      const data = await res.json();
      setMissing(data.missing_country_count);
    } catch (err) {
      logger.error('[CountryBackfill] stats:', err);
      toast.error(`Failed to load stats: ${err.message}`);
    } finally {
      setLoadingStats(false);
    }
  }, []);

  const loadRuns = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/api/admin/users/country-backfill/runs`, { headers: authHeaders() });
      if (!res.ok) return;
      const data = await res.json();
      setRuns(data.runs || []);
    } catch (err) {
      logger.error('[CountryBackfill] runs:', err);
    }
  }, []);

  useEffect(() => { loadStats(); loadRuns(); }, [loadStats, loadRuns]);

  const handleUndoRun = async (runId) => {
    if (!window.confirm('Revert this run? Only users whose country still matches what was applied will be reverted (no one who has manually edited theirs).')) return;
    setUndoLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/admin/users/country-backfill/undo/${runId}`, { method: 'POST', headers: authHeaders() });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || `Undo failed (${res.status})`);
      toast.success(`Reverted ${data.reverted_count} user${data.reverted_count === 1 ? '' : 's'}${data.skipped_user_changed_count ? ` (${data.skipped_user_changed_count} skipped — user edited after backfill)` : ''}`);
      await loadStats(); await loadRuns();
      if (onApplied) onApplied();
    } catch (err) {
      logger.error('[CountryBackfill] undo:', err);
      toast.error(`Undo failed: ${err.message}`);
    } finally {
      setUndoLoading(false);
    }
  };

  const handleRecoveryPreview = async () => {
    setRecoveryBusy(true); setRecoveryPreview(null);
    try {
      const res = await fetch(`${API_URL}/api/admin/users/country-backfill/revert-by-country/preview`, {
        method: 'POST', headers: { ...authHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ country: recoveryCountry, since_minutes: Number(recoverySince) }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || `Preview failed (${res.status})`);
      setRecoveryPreview(data);
      toast.success(`Found ${data.match_count} user${data.match_count === 1 ? '' : 's'} matching ${recoveryCountry}${data.time_filter_applied ? ` updated in the last ${recoverySince} minute(s)` : ''}`);
    } catch (err) {
      logger.error('[CountryBackfill] recovery preview:', err);
      toast.error(`Preview failed: ${err.message}`);
    } finally {
      setRecoveryBusy(false);
    }
  };

  const handleRecoveryApply = async () => {
    if (!recoveryPreview || recoveryPreview.match_count === 0) return;
    if (!window.confirm(`Revert ${recoveryPreview.match_count} user(s) from "${recoveryCountry}" to null? This cannot be auto-undone.`)) return;
    setRecoveryBusy(true);
    try {
      const res = await fetch(`${API_URL}/api/admin/users/country-backfill/revert-by-country/apply`, {
        method: 'POST', headers: { ...authHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ country: recoveryCountry, since_minutes: Number(recoverySince) }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || `Apply failed (${res.status})`);
      toast.success(`Reverted ${data.reverted_count} user${data.reverted_count === 1 ? '' : 's'} from ${recoveryCountry} to null`);
      setRecoveryPreview(null);
      await loadStats();
      if (onApplied) onApplied();
    } catch (err) {
      logger.error('[CountryBackfill] recovery apply:', err);
      toast.error(`Apply failed: ${err.message}`);
    } finally {
      setRecoveryBusy(false);
    }
  };

  const handlePreview = async () => {
    setPreviewLoading(true);
    setPreview(null); setApplyResult(null);
    toast.info('Resolving countries (no DB writes)…');
    try {
      const res = await fetch(`${API_URL}/api/admin/users/country-backfill/preview`, {
        method: 'POST', headers: authHeaders(),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || `Preview failed (${res.status})`);
      setPreview(data);
      toast.success(`Preview complete — ${data.resolved} of ${data.candidates} would be filled in`);
    } catch (err) {
      logger.error('[CountryBackfill] preview:', err);
      toast.error(`Preview failed: ${err.message}`);
    } finally {
      setPreviewLoading(false);
    }
  };

  const handleApply = async () => {
    setConfirming(false);
    setApplyLoading(true);
    setApplyResult(null);
    toast.info('Applying backfill (writing to database)…');
    try {
      const res = await fetch(`${API_URL}/api/admin/users/country-backfill/apply`, {
        method: 'POST', headers: authHeaders(),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || `Apply failed (${res.status})`);
      setApplyResult(data);
      toast.success(`Applied — ${data.applied} user${data.applied === 1 ? '' : 's'} updated`);
      await loadStats();
      await loadRuns();
      if (onApplied) onApplied();
    } catch (err) {
      logger.error('[CountryBackfill] apply:', err);
      toast.error(`Apply failed: ${err.message}`);
    } finally {
      setApplyLoading(false);
    }
  };

  const busy = loadingStats || previewLoading || applyLoading;

  return (
    <div className="bg-gray-800 rounded-xl border border-gray-700 p-4 mb-4" data-testid="country-backfill-panel">
      <div className="flex items-start justify-between gap-3 mb-3 flex-wrap">
        <div className="flex items-start gap-3">
          <div className="bg-blue-500/20 p-2 rounded-lg">
            <Globe className="w-5 h-5 text-blue-400" />
          </div>
          <div>
            <h4 className="text-sm font-bold text-white">Country Backfill</h4>
            <p className="text-xs text-gray-400 mt-0.5">
              Fill in country for users who don&apos;t have one yet, using their login history + IP geolocation.
            </p>
          </div>
        </div>
        <button onClick={loadStats} disabled={busy}
          className="text-xs text-gray-400 hover:text-white inline-flex items-center gap-1"
          data-testid="backfill-refresh-stats" title="Re-count users missing country">
          <RefreshCw className={`w-3 h-3 ${loadingStats ? 'animate-spin' : ''}`} />
          Refresh
        </button>
      </div>

      <div className="flex items-center gap-3 mb-3 flex-wrap">
        <div className="bg-gray-900/50 border border-gray-700 rounded px-3 py-2 text-sm flex items-center gap-2" data-testid="backfill-missing-count">
          {loadingStats && missing === null ? (
            <Loader2 className="w-4 h-4 animate-spin text-gray-400" />
          ) : missing === 0 ? (
            <><CheckCircle2 className="w-4 h-4 text-green-400" /><span className="text-gray-300">All active users have a country set</span></>
          ) : (
            <><AlertTriangle className="w-4 h-4 text-amber-400" /><span className="text-gray-300"><span className="font-bold text-amber-400">{missing}</span> user{missing === 1 ? '' : 's'} missing country</span></>
          )}
        </div>
        <button onClick={handlePreview} disabled={busy || missing === 0}
          className="px-3 py-1.5 bg-gray-700 hover:bg-gray-600 disabled:opacity-40 disabled:cursor-not-allowed text-white text-sm rounded-lg inline-flex items-center gap-2"
          data-testid="backfill-preview-btn">
          {previewLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
          Preview (dry-run)
        </button>
        <button onClick={() => setConfirming(true)} disabled={busy || missing === 0}
          className="px-3 py-1.5 bg-blue-600 hover:bg-blue-700 disabled:opacity-40 disabled:cursor-not-allowed text-white text-sm rounded-lg inline-flex items-center gap-2"
          data-testid="backfill-apply-btn">
          {applyLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Play className="w-4 h-4" />}
          Run Backfill
        </button>
      </div>

      {(previewLoading || applyLoading) && (
        <div className="text-xs text-gray-400 mb-3" data-testid="backfill-progress-hint">
          This may take a few seconds (1.5s per IP geolocation call to stay within the free-tier rate limit).
        </div>
      )}

      {preview && !applyResult && (
        <div className="mb-2">
          <div className="text-xs text-gray-400 mb-2">Dry-run results — no changes have been written:</div>
          <ResultsBlock result={preview} isPreview={true} />
        </div>
      )}

      {applyResult && (
        <div className="mb-2">
          <div className="text-xs text-green-400 mb-2 inline-flex items-center gap-1">
            <CheckCircle2 className="w-3.5 h-3.5" />
            Applied — countries persisted to database:
          </div>
          <ResultsBlock result={applyResult} isPreview={false} />
        </div>
      )}

      {confirming && (
        <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4" data-testid="backfill-confirm-modal">
          <div className="bg-gray-800 border border-gray-700 rounded-xl p-6 max-w-md w-full">
            <h3 className="text-lg font-bold text-white mb-2">Confirm Country Backfill</h3>
            <p className="text-sm text-gray-300 mb-2">
              This will attempt to resolve and persist a country for <span className="font-bold text-amber-400">{missing}</span> user{missing === 1 ? '' : 's'} with missing country data.
            </p>
            <ul className="text-xs text-gray-400 space-y-1 mb-4 list-disc pl-5">
              <li>Will never overwrite a country that&apos;s already set.</li>
              <li>Users whose country can&apos;t be resolved are left unchanged.</li>
              <li>The run is audit-logged with your admin ID.</li>
              <li>Safe to re-run — idempotent.</li>
            </ul>
            <p className="text-xs text-amber-300 bg-amber-500/10 border border-amber-500/30 rounded p-2 mb-4">
              Recommended: run <strong>Preview</strong> first to see what will happen before applying.
            </p>
            <div className="flex gap-2 justify-end">
              <button onClick={() => setConfirming(false)}
                className="px-3 py-1.5 border border-gray-600 text-gray-300 hover:bg-gray-700 text-sm rounded-lg"
                data-testid="backfill-confirm-cancel">
                Cancel
              </button>
              <button onClick={handleApply}
                className="px-3 py-1.5 bg-blue-600 hover:bg-blue-700 text-white text-sm rounded-lg inline-flex items-center gap-2"
                data-testid="backfill-confirm-apply">
                <Play className="w-4 h-4" />Run Backfill
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Backfill history with Undo */}
      {runs.length > 0 && (
        <details className="mt-4 bg-gray-900/40 border border-gray-700 rounded" data-testid="backfill-runs-section">
          <summary className="px-3 py-2 text-sm text-gray-300 cursor-pointer hover:bg-gray-700/30 inline-flex items-center gap-2">
            <History className="w-4 h-4" /> Backfill history ({runs.length} run{runs.length === 1 ? '' : 's'})
          </summary>
          <div className="overflow-x-auto">
            <table className="w-full text-left">
              <thead className="bg-gray-800">
                <tr>
                  <th className="py-2 px-3 text-xs text-gray-400">When</th>
                  <th className="py-2 px-3 text-xs text-gray-400">By</th>
                  <th className="py-2 px-3 text-xs text-gray-400 text-right">Resolved / Applied</th>
                  <th className="py-2 px-3 text-xs text-gray-400">Status</th>
                  <th className="py-2 px-3 text-xs text-gray-400 text-right">Action</th>
                </tr>
              </thead>
              <tbody>
                {runs.map((r) => (
                  <tr key={r.run_id} className="border-b border-gray-700/50" data-testid={`backfill-run-${r.run_id}`}>
                    <td className="py-2 px-3 text-xs text-gray-300">{new Date(r.timestamp).toLocaleString()}</td>
                    <td className="py-2 px-3 text-xs text-gray-400">{r.admin_name || r.admin_id}</td>
                    <td className="py-2 px-3 text-xs text-gray-300 text-right">{r.resolved} / {r.applied}</td>
                    <td className="py-2 px-3 text-xs">
                      {r.undone
                        ? <span className="text-gray-500 italic">undone</span>
                        : <span className="text-green-400">active</span>}
                    </td>
                    <td className="py-2 px-3 text-right">
                      <button
                        onClick={() => handleUndoRun(r.run_id)}
                        disabled={r.undone || undoLoading || r.applied === 0}
                        className="px-2 py-1 bg-amber-600/20 hover:bg-amber-600/30 text-amber-400 rounded text-xs inline-flex items-center gap-1 disabled:opacity-40 disabled:cursor-not-allowed border border-amber-600/50"
                        data-testid={`backfill-undo-${r.run_id}`}
                      >
                        <Undo2 className="w-3 h-3" /> Undo
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </details>
      )}

      {/* One-shot recovery for the legacy unrecorded backfill */}
      <details
        open={showRecovery}
        onToggle={(e) => setShowRecovery(e.currentTarget.open)}
        className="mt-3 bg-gray-900/40 border border-amber-500/30 rounded"
        data-testid="backfill-recovery-section"
      >
        <summary className="px-3 py-2 text-sm text-amber-300 cursor-pointer hover:bg-amber-500/5 inline-flex items-center gap-2">
          <ShieldAlert className="w-4 h-4" /> Legacy recovery: revert wrongly-resolved countries
        </summary>
        <div className="p-3 space-y-2">
          <p className="text-xs text-gray-300">
            Use this if a previous backfill (before run-recording existed) set a wrong country (e.g. proxy IP resolved everyone to USA). Lists active users with the given country — set them back to null so the dashboard banner can prompt them to fix it themselves.
          </p>
          <div className="flex flex-wrap gap-2 items-end">
            <div>
              <label className="block text-[10px] text-gray-400 mb-1">Country to revert</label>
              <input value={recoveryCountry} onChange={(e) => { setRecoveryCountry(e.target.value); setRecoveryPreview(null); }}
                className="px-2 py-1 bg-gray-800 border border-gray-700 text-white text-sm rounded w-56"
                placeholder="United States" data-testid="recovery-country-input" />
            </div>
            <div>
              <label className="block text-[10px] text-gray-400 mb-1">Time filter (minutes — 0 = no filter)</label>
              <input type="number" min="0" value={recoverySince} onChange={(e) => { setRecoverySince(e.target.value); setRecoveryPreview(null); }}
                className="px-2 py-1 bg-gray-800 border border-gray-700 text-white text-sm rounded w-32"
                data-testid="recovery-since-input" />
            </div>
            <button onClick={handleRecoveryPreview} disabled={recoveryBusy || !recoveryCountry.trim()}
              className="px-3 py-1.5 bg-gray-700 hover:bg-gray-600 disabled:opacity-40 text-white text-sm rounded inline-flex items-center gap-1"
              data-testid="recovery-preview-btn">
              {recoveryBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
              Preview
            </button>
            {recoveryPreview && recoveryPreview.match_count > 0 && (
              <button onClick={handleRecoveryApply} disabled={recoveryBusy}
                className="px-3 py-1.5 bg-red-600 hover:bg-red-700 disabled:opacity-40 text-white text-sm rounded inline-flex items-center gap-1"
                data-testid="recovery-apply-btn">
                <Undo2 className="w-4 h-4" /> Revert {recoveryPreview.match_count} to null
              </button>
            )}
          </div>
          {recoveryPreview && (
            <div className="text-xs text-gray-300 mt-2" data-testid="recovery-preview-results">
              <div className="mb-2">
                Found <strong className="text-amber-400">{recoveryPreview.match_count}</strong> user(s) with country = <code>{recoveryPreview.country}</code>
                {recoveryPreview.time_filter_applied
                  ? <> updated since <code>{new Date(recoveryPreview.cutoff).toLocaleString()}</code></>
                  : <> (no time filter applied)</>}.
                {recoveryPreview.match_count === 0 && <span className="text-gray-500 italic"> Nothing to revert.</span>}
              </div>
              {recoveryPreview.matches && recoveryPreview.matches.length > 0 && (
                <div className="bg-gray-800/50 border border-gray-700 rounded max-h-[300px] overflow-y-auto">
                  <table className="w-full text-left">
                    <thead className="bg-gray-800 sticky top-0">
                      <tr>
                        <th className="py-1.5 px-2 text-[10px] text-gray-400">User ID</th>
                        <th className="py-1.5 px-2 text-[10px] text-gray-400">Created</th>
                        <th className="py-1.5 px-2 text-[10px] text-gray-400">Updated</th>
                      </tr>
                    </thead>
                    <tbody>
                      {recoveryPreview.matches.map((u) => (
                        <tr key={u.user_id} className="border-b border-gray-700/50">
                          <td className="py-1.5 px-2 text-[11px] font-mono text-gray-300">{u.user_id}</td>
                          <td className="py-1.5 px-2 text-[11px] text-gray-400">{u.created_at ? new Date(u.created_at).toLocaleDateString() : '—'}</td>
                          <td className="py-1.5 px-2 text-[11px] text-gray-400">{u.updated_at ? new Date(u.updated_at).toLocaleString() : '—'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}
        </div>
      </details>
    </div>
  );
};
